import axios from "axios";
import React, { createContext, useState, useEffect } from "react";

import { clearUserData } from "../utils/clearUserData";
import { resetUser as posthogResetUser } from "../utils/posthog";
import {
  baseURL,
  startProactiveRefresh,
  clearTokens,
} from "../utils/ApiClient";

export const AuthContext = createContext({
  isLoggedIn: false,
  isCheckingAuth: true,
  setNangoSessionTokenLocalStorage: () => {},
  nangoSessionToken: null,
  setAuthToken: () => {},
});

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [nangoSessionToken, setNangoSessionToken] = useState();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [teamId, setTeamId] = useState(null);
  const [userId, setUserId] = useState(null);

  // Note: Named "LocalStorage" for legacy reasons but now just sets state
  const setNangoSessionTokenLocalStorage = setNangoSessionToken;

  const logout = async () => {
    // Clear all local state before network calls (defense-in-depth:
    // if tab closes during the await, localStorage is already clean)
    clearTokens();
    clearUserData();
    posthogResetUser();

    // Call logout endpoint to clear HttpOnly cookies
    try {
      await axios.post(`${baseURL}/logout/`, {}, { withCredentials: true });
    } catch (error) {
      // Ignore errors - we're logging out anyway
      console.log("Logout endpoint call failed (non-critical):", error.message);
    }

    nukeUser();
  };

  const nukeUser = () => {
    setUser(null);
    setTeam(null);
    setTeamId(null);
    setUserId(null);
    setIsLoggedIn(false);
    setNangoSessionToken(null);
  };

  // setAuthToken is used after login to set user state from the login response
  // No localStorage storage - cookies handle auth
  const setAuthToken = (userData) => {
    if (!userData || !userData.id) {
      console.error("Invalid user data provided to setAuthToken");
      return;
    }

    setUser(userData);
    setUserId(userData.id);
    setTeamId(userData.team_id);
    setIsLoggedIn(true);
    setIsCheckingAuth(false);

    // Start proactive token refresh cycle
    startProactiveRefresh();
  };

  // Initial auth check - verify cookie-based auth by calling /users/me/
  useEffect(() => {
    const checkAuth = async () => {
      console.log("AuthProvider: Checking cookie-based auth...");

      try {
        const response = await axios.get(`${baseURL}/users/me/`, {
          withCredentials: true,
        });
        if (response.status === 200 && response.data) {
          console.log("AuthProvider: Cookie-based auth successful");
          setUser(response.data);
          setUserId(response.data.id);
          if (response.data.team_id) {
            setTeamId(response.data.team_id);
          }
          setIsLoggedIn(true);

          // Start proactive token refresh cycle for existing sessions
          startProactiveRefresh();
        }
      } catch (error) {
        // No valid auth
        console.log("AuthProvider: No valid auth found");
      }
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isCheckingAuth,
        setAuthToken,
        setNangoSessionTokenLocalStorage,
        logout,
        user,
        nangoSessionToken,
        setUser,
        setTeam,
        team,
        teamId,
        userId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
